import type { SceneComposition } from '../../types/engine.types';
import { BaseSceneBuilder } from '../scene-builder.interface';
import type { ApplianceInput, ApplianceSceneBuilderConfig } from './types';
import { DEFAULT_APPLIANCE_CONFIG } from './types';
import { buildCfn } from './functions';

/**
 * ApplianceSceneBuilder converts appliance configuration into scene compositions.
 *
 * Creates an appliance container group and loads the 3D model via ModelManager.
 *
 * @example
 * ```typescript
 * const builder = new ApplianceSceneBuilder({
 *   basePath: '/assets/',
 *   renderQuality: 7,
 *   textureManager: engine.textureManager,
 *   modelManager: engine.modelManager
 * });
 *
 * const composition = await builder.build({
 *   modelPath: 'models/grill.gltf',
 *   id: 'grill-1',
 *   position: { x: 0, y: 0, z: 0 }
 * });
 *
 * engine.registerBuilder('appliance', builder);
 * ```
 */
export class ApplianceSceneBuilder extends BaseSceneBuilder<ApplianceInput> {
	readonly name = 'appliance';
	#config: ApplianceSceneBuilderConfig;

	constructor(config: Partial<ApplianceSceneBuilderConfig> = {}) {
		super();
		this.#config = { ...DEFAULT_APPLIANCE_CONFIG, ...config };
	}

	// ========== CFN Accessors ==========

	/** @internal Access to config for CFN functions */
	get configAccess(): ApplianceSceneBuilderConfig {
		return this.#config;
	}

	// ========== Public API ==========

	/**
	 * Update configuration
	 */
	updateConfig(config: Partial<ApplianceSceneBuilderConfig>): void {
		this.#config = { ...this.#config, ...config };
	}

	/**
	 * Build scene composition from appliance configuration
	 */
	async build (input: ApplianceInput): Promise<SceneComposition> {
		return buildCfn(this, { input });
	}
}
